import { zodResolver } from '@hookform/resolvers/zod';
import { defaultValue } from 'Pages/reports/[submodules]/history/schemas/history-report-schemas';
import {
  historyReportFilterSchema,
  ageRangeOptions,
} from 'Pages/reports/[submodules]/history/schemas/history-report-schemas';
import { useForm } from 'react-hook-form';
import useTerminalsApi from './useTerminalsApi';
import type { HistoryReportFilterI } from 'types';
import useDepartmentsApi from './useDepartmentsApi';
import useJobTitlesApi from './useJobTitlesApi';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from './useDebounce';
import { useEffect } from 'react';
import { formatDateToISO } from 'Utils/format-date';

interface Props {
  onSearch: (filters: HistoryReportFilterI) => void;
}

const useHistoryReportFilters = ({ onSearch }: Props) => {
  const { t2 } = useTranslation('history_reports');

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { isDirty },
    watch,
  } = useForm({
    resolver: zodResolver(historyReportFilterSchema),
    defaultValues: defaultValue,
  });

  const { data: terminalsData, loading: terminalsLoading, error: terminalsError } = useTerminalsApi({ init: true });

  const {
    data: departmentsData,
    error: departmentsError,
    loading: departmentsLoading,
  } = useDepartmentsApi({ init: true });

  const { data: jobTitlesData, loading: jobTitlesLoading, error: jobTitlesError } = useJobTitlesApi({ init: true });

  const handleReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  const handleAgeRangeChange = (value: number | null) => {
    setValue('ageRange', value);
    if (value === null) {
      setValue('ageMin', null);
      setValue('ageMax', null);
      return;
    }

    const [min, max] = ageRangeOptions[value as keyof typeof ageRangeOptions];
    setValue('ageMin', min);
    setValue('ageMax', max);
  };

  // Filter out ageRange before sending to backend
  const getFiltersForApi = () => {
    const values = getValues();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ageRange, entryDateStart, ...filters } = values;
    return {
      ...filters,
      entryDateStart: formatDateToISO(entryDateStart),
    };
  };

  // Watch for changes in the name field
  const nameValue = watch('search');
  const debouncedName = useDebounce(nameValue, 500);

  // Effect to handle debounced search
  useEffect(() => {
    if (debouncedName !== undefined) {
      const values = getFiltersForApi();
      onSearch(values);
    }
  }, [debouncedName]);

  // Handle other filter changes immediately
  const handleFilterChange = () => {
    const values = getFiltersForApi();
    onSearch(values);
  };

  return {
    control,
    reset,
    setValue,
    getValues,
    isDirty,
    handleReset,
    handleAgeRangeChange,
    t2,
    // Data
    terminalsData,
    departmentsData,
    jobTitlesData,
    // Loading
    terminalsLoading,
    departmentsLoading,
    jobTitlesLoading,
    // Error
    terminalsError,
    departmentsError,
    jobTitlesError,
    handleFilterChange,
    getFiltersForApi,
  };
};

export default useHistoryReportFilters;
